import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API_URL } from "../api";
import MobileHeader from "../mobile/MobileHeader";
import MobileBottomNav, { NAV_HEIGHT } from "../mobile/MobileBottomNav";
import { getImageUrl } from "../utils/imageUrl";

import { getLocalizedName } from "../utils/localize";
/**
 * ZEPTO/BLINKIT-STYLE REDESIGN
 * ----------------------------
 * - Sidebar switched from plain text buttons to circular category avatars
 *   (the instantly-recognizable quick-commerce pattern).
 * - Cards now show out-of-stock / low-stock badges, a unit line ("500 g",
 *   "1 kg"), and an ADD button that morphs into a +/- stepper in place
 *   instead of the button just disappearing.
 * - Every interactive element has a press animation, cards fade/slide in
 *   staggered on load, and quantity changes pulse - the "juice" that makes
 *   Zepto/Blinkit feel snappy instead of static.
 * - Native alert() replaced with a small inline toast so nothing blocks
 *   the UI thread or looks jarring.
 * - Guards against race conditions: request sequencing on the cart fetch,
 *   and per-item pending locks so a double-tap or a still-in-flight ADD
 *   can't fire a stray request against an id the server doesn't know yet.
 */
export default function CategoryItems() {
  useTranslation();
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedSub, setSelectedSub] = useState("All");
  const [headerHeight, setHeaderHeight] = useState(112);
  const [pendingIds, setPendingIds] = useState({});
  const [brokenImages, setBrokenImages] = useState({});
  const [toast, setToast] = useState(null);

  const getUserId = () => {
    try { return localStorage.getItem("phone") || "guest"; } catch (_) { return "guest"; }
  };
  const user_id = getUserId();
  const isLoggedIn = () => {
    try { return localStorage.getItem("isLoggedIn") === "true"; } catch (_) { return false; }
  };

  const showToast = (message) => {
    setToast({ id: Date.now(), message });
  };

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  // Guards against an older, slower cart response landing after a newer one
  // and overwriting it with stale data.
  const cartFetchSeq = useRef(0);

  const fetchCartData = useCallback(async () => {
    if (user_id === "guest") return;
    const seq = ++cartFetchSeq.current;
    try {
      const res = await axios.get(`${API_URL}/cart/${user_id}`);
      if (seq !== cartFetchSeq.current) return;
      if (res.data.success) setCartItems(res.data.cart);
    } catch (err) {
      console.log("Cart fetch error:", err);
    }
  }, [user_id]);

  const fetchItems = useCallback(() => {
    setLoading(true);
    setError(false);
    axios.get(`${API_URL}/items/${categoryId}`)
      .then((res) => {
        if (res.data.success) setItems(res.data.items);
        else setError(true);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [categoryId]);

  useEffect(() => {
    fetchCartData();
    fetchItems();
  }, [categoryId, fetchCartData, fetchItems]);

  // A broken image from the previous category shouldn't linger onto a new
  // product that happens to reuse the same id.
  useEffect(() => {
    setBrokenImages({});
  }, [categoryId]);

  const isFlowers = categoryId === "flowers";

  const subCategories = useMemo(
    () => (isFlowers ? [] : ["All", ...new Set(items.map((i) => i.subcategory || "General"))]),
    [items, isFlowers]
  );

  // Keep the selected tab valid across category switches / refreshes, so it
  // can't silently point at a subcategory that no longer exists and show a
  // false "no items found".
  useEffect(() => {
    setSelectedSub((prev) => (isFlowers ? "All" : (subCategories.includes(prev) ? prev : "All")));
  }, [categoryId, subCategories, isFlowers]);

  const filteredItems = (selectedSub === "All" || isFlowers) ? items : items.filter((i) => (i.subcategory || "General") === selectedSub);

  const getSubcategoryImage = (subName) => {
    if (subName === "All" && items.length > 0) return getImageUrl(items[0].image);
    const item = items.find((i) => (i.subcategory || "General").toLowerCase() === subName.toLowerCase());
    return item ? getImageUrl(item.image) : "";
  };

  const goSearch = () => {
    if (search.trim() !== "") navigate(`/search?q=${encodeURIComponent(search.trim())}`);
  };

  const formatTitle = (str) => (str ? str.replace(/-/g, " ").toUpperCase() : "");

  const addToCart = async (product) => {
    if (!isLoggedIn() || user_id === "guest") {
      showToast("Please login to add items.");
      return;
    }
    const pendingKey = `add-${product.id}`;
    if (pendingIds[pendingKey]) return; // ignore double taps while the first request is in flight
    setPendingIds((p) => ({ ...p, [pendingKey]: true }));

    const tempId = `temp-${product.id}`;
    setCartItems((prev) => [...prev, { id: tempId, product_id: product.id, category: categoryId, quantity: 1 }]);
    window.dispatchEvent(new Event("cartUpdated"));

    try {
      const res = await axios.post(`${API_URL}/cart/add`, { user_id, product_id: product.id, category: categoryId });
      const real = res.data?.cartItem;
      setCartItems((prev) => prev.map((i) => (i.id === tempId
        ? { ...i, id: real ? real.id : i.id, quantity: real ? real.quantity : i.quantity }
        : i)));
    } catch (err) {
      setCartItems((prev) => prev.filter((i) => i.id !== tempId));
      showToast("Couldn't add item — please try again.");
    } finally {
      window.dispatchEvent(new Event("cartUpdated"));
      setPendingIds((p) => {
        const next = { ...p };
        delete next[pendingKey];
        return next;
      });
    }
  };

  const updateQty = async (cart_id, action) => {
    if (typeof cart_id === "string" && cart_id.startsWith("temp-")) return; // real id hasn't come back yet
    if (pendingIds[cart_id]) return;
    const item = cartItems.find((i) => i.id === cart_id);
    if (!item) return;
    const prevSnapshot = cartItems;
    setPendingIds((p) => ({ ...p, [cart_id]: true }));

    if (action === "decrease" && item.quantity <= 1) {
      setCartItems((prev) => prev.filter((i) => i.id !== cart_id));
    } else {
      setCartItems((prev) => prev.map((i) => i.id === cart_id
        ? { ...i, quantity: action === "increase" ? i.quantity + 1 : i.quantity - 1 }
        : i));
    }
    window.dispatchEvent(new Event("cartUpdated"));

    try {
      if (action === "decrease" && item.quantity <= 1) {
        await axios.delete(`${API_URL}/cart/${cart_id}`);
      } else {
        await axios.put(`${API_URL}/cart/${action}/${cart_id}`);
      }
    } catch (err) {
      setCartItems(prevSnapshot);
      showToast("Couldn't update quantity — please try again.");
    } finally {
      window.dispatchEvent(new Event("cartUpdated"));
      setPendingIds((p) => {
        const next = { ...p };
        delete next[cart_id];
        return next;
      });
    }
  };

  const bottomReserved = `calc(${NAV_HEIGHT}px + env(safe-area-inset-bottom, 0px))`;

  return (
    <div style={styles.container}>
      <MobileHeader
        searchValue={search}
        setSearchValue={setSearch}
        onSearch={goSearch}
        showLogo={false}
        showTitleBar
        title={formatTitle(categoryId)}
        activeTab="category"
        isLoggedIn={isLoggedIn()}
        onHeightChange={setHeaderHeight}
      />

      <div
        style={{
          ...styles.mainWrapper,
          top: `${headerHeight}px`,
          bottom: bottomReserved,
        }}
      >
        {!isFlowers && (
          <div style={styles.sidebar}>
            {subCategories.map((sub) => {
              const subImage = getSubcategoryImage(sub);
              const active = selectedSub === sub;
              return (
                <div
                  key={sub}
                  style={styles.sideItem}
                  className="ci-side-item"
                  onClick={() => setSelectedSub(sub)}
                  role="button"
                  tabIndex={0}
                  aria-pressed={active}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelectedSub(sub); } }}
                >
                  <div
                    className="ci-cat-circle"
                    style={{ ...styles.catCircle, border: active ? `2px solid ${BRAND_DARK}` : "2px solid transparent", transform: active ? "scale(1.06)" : "scale(1)" }}
                  >
                    {subImage ? (
                      <img src={subImage} alt={sub} style={styles.catImage} loading="lazy" />
                    ) : (
                      <div style={styles.catPlaceholder} />
                    )}
                  </div>
                  <span style={active ? styles.catLabelActive : styles.catLabel}>{sub}</span>
                </div>
              );
            })}
          </div>
        )}

        <div style={styles.scrollArea}>
          {loading ? (
            <div style={styles.grid}>
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} style={styles.skeletonCard}>
                  <div style={styles.skeletonImage} />
                  <div style={styles.skeletonLine} />
                  <div style={{ ...styles.skeletonLine, width: "50%" }} />
                </div>
              ))}
            </div>
          ) : error ? (
            <div style={styles.statusWrap} className="ci-fade-in">
              <div style={styles.statusIcon} aria-hidden="true">⚠️</div>
              <p style={styles.statusText}>Couldn't load items right now.</p>
              <button style={styles.retryBtn} className="ci-press" onClick={fetchItems}>Try again</button>
            </div>
          ) : filteredItems.length === 0 ? (
            <div style={styles.statusWrap} className="ci-fade-in">
              <div style={styles.statusIcon} aria-hidden="true">🔍</div>
              <p style={styles.statusText}>No items found{selectedSub !== "All" ? ` in "${selectedSub}"` : ""}.</p>
              {selectedSub !== "All" && (
                <button style={styles.retryBtn} className="ci-press" onClick={() => setSelectedSub("All")}>Show all</button>
              )}
            </div>
          ) : (
            <div style={styles.grid}>
              {filteredItems.map((product, idx) => {
                const cartItem = cartItems.find((c) => c.product_id === product.id && (c.category ?? categoryId) === categoryId);
                const stockNum = Number(product.stock);
                const hasStockInfo = product.stock !== undefined && product.stock !== null && !Number.isNaN(stockNum);
                const outOfStock = hasStockInfo && stockNum <= 0;
                const lowStock = hasStockInfo && !outOfStock && stockNum <= LOW_STOCK_THRESHOLD;
                const atStockLimit = hasStockInfo && !!cartItem && stockNum > 0 && cartItem.quantity >= stockNum;
                const addPending = !!pendingIds[`add-${product.id}`];
                const imgBroken = !!brokenImages[product.id];

                return (
                  <div
                    key={product.id}
                    className="ci-card"
                    style={{
                      ...(outOfStock ? { ...styles.card, ...styles.cardOutOfStock } : styles.card),
                      animationDelay: `${Math.min(idx, 9) * 30}ms`,
                    }}
                  >
                    <div style={styles.imgWrapper}>
                      {imgBroken ? (
                        <div style={styles.imgFallback} aria-hidden="true">🖼️</div>
                      ) : (
                        <img
                          src={getImageUrl(product.image)}
                          style={outOfStock ? { ...styles.image, ...styles.imageOutOfStock } : styles.image}
                          alt={getLocalizedName(product.name)}
                          loading="lazy"
                          onError={() => setBrokenImages((p) => ({ ...p, [product.id]: true }))}
                        />
                      )}
                      {outOfStock && <div style={styles.outOfStockBadge}>Out of Stock</div>}
                      {lowStock && <div style={styles.lowStockBadge}>{stockNum === 1 ? "1 left" : `${stockNum} left`}</div>}
                    </div>

                    <h4 style={styles.name}>{getLocalizedName(product.name)}</h4>
                    {product.quantity && <span style={styles.unit}>{product.quantity}</span>}

                    <div style={styles.bottomRow}>
                      <p style={styles.price}>₹{product.price}</p>

                      {outOfStock ? (
                        <span style={styles.outOfStockText}>Unavailable</span>
                      ) : cartItem ? (
                        <div style={styles.qtyBox} className="ci-qty-box">
                          <button
                            style={styles.btn}
                            className="ci-press"
                            disabled={!!pendingIds[cartItem.id] || addPending}
                            onClick={() => updateQty(cartItem.id, "decrease")}
                            aria-label={`Decrease quantity of ${getLocalizedName(product.name)}`}
                          >−</button>
                          <span
                            key={`${cartItem.id}-${cartItem.quantity}`}
                            style={styles.qty}
                            className="ci-qty-pulse"
                            aria-live="polite"
                          >{cartItem.quantity}</span>
                          <button
                            style={{ ...styles.btn, ...((atStockLimit || addPending) ? styles.btnDisabled : {}) }}
                            className="ci-press"
                            disabled={!!pendingIds[cartItem.id] || addPending || atStockLimit}
                            title={atStockLimit ? `Only ${stockNum} in stock` : undefined}
                            onClick={() => updateQty(cartItem.id, "increase")}
                            aria-label={`Increase quantity of ${getLocalizedName(product.name)}`}
                          >+</button>
                        </div>
                      ) : (
                        <button
                          style={styles.addBtn}
                          className="ci-press"
                          disabled={addPending}
                          onClick={() => addToCart(product)}
                          aria-label={`Add ${getLocalizedName(product.name)} to cart`}
                        >
                          {addPending ? "..." : "ADD"}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {toast && (
        <div key={toast.id} style={styles.toast} className="ci-toast">
          {toast.message}
        </div>
      )}

      <MobileBottomNav viewCartBar />
    </div>
  );
}

const BRAND = "#8ec5fc";
const BRAND_DARK = "#2563eb";
const GRADIENT = "linear-gradient(135deg, #8ec5fc 0%, #6badf5 100%)";
const LOW_STOCK_THRESHOLD = 5;

const styles = {
  container: {
    position: "fixed",
    top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: "transparent",
    overflow: "hidden",
  },
  mainWrapper: {
    position: "absolute",
    left: 0, right: 0,
    display: "flex",
    boxSizing: "border-box",
    overflow: "hidden",
    backgroundColor: "#f5f7fa",
  },
  sidebar: {
    width: "clamp(68px, 21vw, 85px)",
    height: "100%",
    backgroundColor: "#fff",
    borderRight: "1px solid #eef1f5",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "20px 0",
    overflowY: "auto",
    boxSizing: "border-box",
  },
  sideItem: { marginBottom: "20px", textAlign: "center", cursor: "pointer", width: "100%" },
  catCircle: { width: "62%", aspectRatio: "1 / 1", borderRadius: "50%", backgroundColor: "#eef1f5", margin: "0 auto 6px", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", boxSizing: "border-box" },
  catImage: { width: "100%", height: "100%", objectFit: "cover" },
  catPlaceholder: { width: "100%", height: "100%", backgroundColor: "#dbe4f0" },
  catLabel: { fontSize: "11px", color: "#667085", fontWeight: "600", display: "block", lineHeight: 1.3 },
  catLabelActive: { fontSize: "11px", color: BRAND_DARK, fontWeight: "700", display: "block", lineHeight: 1.3 },

  scrollArea: {
    flex: 1,
    height: "100%",
    minWidth: 0,
    padding: "15px",
    paddingBottom: "20px",
    boxSizing: "border-box",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
  },

  grid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "22px 14px" },
  card: { backgroundColor: "transparent", padding: 0, borderRadius: 0, boxShadow: "none", display: "flex", flexDirection: "column", minWidth: 0, overflow: "visible" },
  cardOutOfStock: { opacity: 0.6 },
  imgWrapper: { width: "100%", aspectRatio: "1.0", overflow: "hidden", borderRadius: "12px", marginBottom: "8px", backgroundColor: "#eef1f5", position: "relative" },
  image: { width: "100%", height: "100%", objectFit: "cover" },
  imageOutOfStock: { filter: "grayscale(1)" },
  imgFallback: { width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "28px", backgroundColor: "#eef1f5", color: "#aab3c0" },
  outOfStockBadge: { position: "absolute", top: "6px", left: "6px", backgroundColor: "rgba(0,0,0,0.75)", color: "#fff", fontSize: "9px", fontWeight: "bold", padding: "3px 6px", borderRadius: "5px", textTransform: "uppercase", letterSpacing: "0.2px" },
  lowStockBadge: { position: "absolute", top: "6px", left: "6px", backgroundColor: "#ff6b35", color: "#fff", fontSize: "9px", fontWeight: "bold", padding: "3px 6px", borderRadius: "5px", letterSpacing: "0.2px" },
  outOfStockText: { fontSize: "11px", fontWeight: "bold", color: "#e53935" },

  name: { fontSize: "13.5px", margin: "0 0 3px 0", fontWeight: "600", color: "#1a1a1a", whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.35 },
  unit: { fontSize: "12px", color: "#8a94a3", margin: "0 0 10px 0", fontWeight: "500", display: "block" },

  bottomRow: { display: "flex", justifyContent: "space-between", alignItems: "center", gap: "6px", flexWrap: "wrap", rowGap: "6px", minWidth: 0, marginTop: "auto" },
  price: { fontWeight: "900", fontSize: "14px", margin: 0, color: "#111", flexShrink: 0 },
  addBtn: { padding: "7px 14px", border: `1.5px solid ${BRAND}`, backgroundColor: "#eaf1fe", color: BRAND_DARK, borderRadius: "8px", fontWeight: "700", fontSize: "12px", cursor: "pointer", flexShrink: 0, minWidth: "52px" },
  btnDisabled: { opacity: 0.4, cursor: "not-allowed" },
  qtyBox: { display: "flex", alignItems: "center", gap: "4px", background: GRADIENT, borderRadius: "8px", padding: "3px 6px", flexShrink: 0, boxSizing: "border-box" },
  btn: { border: "none", background: "none", color: "#fff", fontWeight: "bold", cursor: "pointer", fontSize: "16px", padding: "0 3px", lineHeight: 1, flexShrink: 0 },
  qty: { fontSize: "13px", fontWeight: "bold", color: "#fff", minWidth: "12px", textAlign: "center", flexShrink: 0, display: "inline-block" },

  statusWrap: { textAlign: "center", padding: "30px", width: "100%" },
  statusIcon: { fontSize: "32px", marginBottom: "8px" },
  statusText: { textAlign: "center", color: "#888", padding: "0 0 12px", width: "100%", margin: 0 },
  retryBtn: { border: `1.5px solid ${BRAND}`, background: "#eaf1fe", color: BRAND_DARK, borderRadius: "8px", padding: "8px 18px", fontWeight: "700", fontSize: "13px", cursor: "pointer" },

  skeletonCard: { backgroundColor: "transparent", padding: 0 },
  skeletonImage: { width: "100%", aspectRatio: "1.0", borderRadius: "12px", marginBottom: "8px", background: "linear-gradient(90deg, #eef1f5 25%, #f7f9fb 37%, #eef1f5 63%)", backgroundSize: "400% 100%", animation: "catSkeleton 1.4s ease infinite" },
  skeletonLine: { height: "12px", borderRadius: "6px", marginBottom: "8px", width: "80%", background: "linear-gradient(90deg, #eef1f5 25%, #f7f9fb 37%, #eef1f5 63%)", backgroundSize: "400% 100%", animation: "catSkeleton 1.4s ease infinite" },

  toast: { position: "fixed", left: "50%", bottom: `calc(${NAV_HEIGHT}px + env(safe-area-inset-bottom, 0px) + 14px)`, transform: "translateX(-50%)", backgroundColor: "rgba(20,20,20,0.92)", color: "#fff", fontSize: "13px", fontWeight: "600", padding: "10px 18px", borderRadius: "999px", boxShadow: "0 4px 16px rgba(0,0,0,0.2)", zIndex: 2500, maxWidth: "85%", textAlign: "center" },
};

if (typeof document !== "undefined" && !document.getElementById("cat-skeleton-kf")) {
  const styleTag = document.createElement("style");
  styleTag.id = "cat-skeleton-kf";
  styleTag.textContent = `
    @keyframes catSkeleton { 0% { background-position: 100% 0; } 100% { background-position: 0 0; } }

    @keyframes ci-fadeInUp {
      from { opacity: 0; transform: translateY(8px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes ci-pulse {
      0% { transform: scale(1); }
      40% { transform: scale(1.28); }
      100% { transform: scale(1); }
    }
    @keyframes ci-fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    @keyframes ci-toastIn {
      from { opacity: 0; transform: translate(-50%, 8px); }
      to { opacity: 1; transform: translate(-50%, 0); }
    }

    .ci-card { animation: ci-fadeInUp 0.32s ease both; transition: transform 0.15s ease, box-shadow 0.15s ease; }
    .ci-card:active { transform: scale(0.98); }

    .ci-press { transition: transform 0.12s ease, filter 0.12s ease; }
    .ci-press:active:not(:disabled) { transform: scale(0.9); filter: brightness(0.96); }
    .ci-press:disabled { cursor: not-allowed; }

    .ci-qty-box { transition: background-color 0.2s ease; }
    .ci-qty-pulse { animation: ci-pulse 0.26s ease; }

    .ci-side-item { transition: transform 0.15s ease; }
    .ci-side-item:active { transform: scale(0.95); }
    .ci-cat-circle { transition: transform 0.2s ease, border-color 0.2s ease; }

    .ci-fade-in { animation: ci-fadeIn 0.3s ease both; }
    .ci-toast { animation: ci-toastIn 0.22s ease both; left: 50%; }

    button:focus-visible, [role="button"]:focus-visible {
      outline: 2px solid ${BRAND_DARK};
      outline-offset: 2px;
    }

    @media (prefers-reduced-motion: reduce) {
      .ci-card, .ci-press, .ci-qty-pulse, .ci-side-item, .ci-cat-circle, .ci-fade-in, .ci-toast {
        animation: none !important;
        transition: none !important;
      }
    }
  `;
  document.head.appendChild(styleTag);
}